import { NgClass, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostBinding, Input, OnChanges, OnInit } from '@angular/core';

import { OfferSource } from '@cds';
import { PlayerStats } from '@cds/statistics/player';
import { EpcotConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { StringDictionary } from '@frontend/sports/common/core/utils/extended-types';
import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import { ValueSliderDirective } from '@frontend/sports/common/ui/value-slider';
import { isNumber } from 'lodash-es';

@Component({
    selector: 'ms-player-stats',
    templateUrl: './player-stats.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgIf, ValueSliderDirective, NgClass],
})
export class PlayerStatsComponent implements OnInit, OnChanges {
    @HostBinding('class') get className(): string {
        return this.isEpcotEnabled ? 'player-stats' : 'player-stats-container';
    }

    @Input() playerStats: PlayerStats[];
    @Input() playerStatsProps: string[];
    @Input() playerIds: number[];
    @Input() sportId: number;
    @Input() source?: OfferSource;

    private siteCorePlayerStats: StringDictionary<string>;
    statsIndicator: string;
    isEpcotEnabled: boolean;
    private readonly INCREASED_CLASS = 'stat-increased';
    private readonly DECREASED_CLASS = 'stat-decreased';
    playerStatsData = {
        statsText: '',
        statsValue: 0,
    };

    constructor(
        private siteCore: Sitecore,
        private changeDetectionRef: ChangeDetectorRef,
        private epcotConfig: EpcotConfig,
    ) {
        this.isEpcotEnabled = this.epcotConfig.isEnabled;
    }

    ngOnInit(): void {
        this.siteCorePlayerStats = this.siteCore.scoreboardPlayerStats[SportConstant[this.sportId].toString().toLowerCase()] || {};
        this.getPlayerStats(this.playerStats);
    }

    ngOnChanges(changes: ISimpleChanges<PlayerStatsComponent>): void {
        if (changes.playerStats && !changes.playerStats.firstChange) {
            const currentStatValue = this.getStatsValue(changes.playerStats.currentValue);

            if (currentStatValue) {
                if (currentStatValue > this.playerStatsData.statsValue) {
                    this.statsIndicator = this.INCREASED_CLASS;
                } else if (currentStatValue < this.playerStatsData.statsValue) {
                    this.statsIndicator = this.DECREASED_CLASS;
                }
                this.playerStatsData.statsValue = currentStatValue;
                this.changeDetectionRef.markForCheck();
            }
        }

        if (changes.playerIds && !changes.playerIds.firstChange) {
            this.getPlayerStats(this.playerStats);
        }
    }

    private getPlayerStats(playerStats: PlayerStats[]): void {
        const selectedStatName = this.playerIds.length > 1 ? 'Combined' + this.playerStatsProps.join('') : this.playerStatsProps.join('');
        this.playerStatsData = {
            statsText: this.siteCorePlayerStats[selectedStatName] || '',
            statsValue: this.getStatsValue(playerStats),
        };
    }

    private getStatsValue(playerStats: PlayerStats[]): number {
        let statData = 0;
        const filteredStats = playerStats.find((x) => x.source === this.source);

        if (!filteredStats) {
            return statData;
        }

        this.playerIds.forEach((player) => {
            const playerStat = filteredStats.stats[player];
            if (playerStat) {
                const count = this.playerStatsProps.length;

                let statValue = this.playerStatsProps.reduce((result, stat) => result + (playerStat[stat.toLowerCase()] || 0), 0);

                // When multiple stats are present, calculate the average of the stats.
                if (count > 1 && isNumber(statValue)) {
                    statValue = Math.round(statValue / count);
                }
                statData += statValue;
            }
        });

        return statData;
    }
}
